import type {
	SelectFilterOption,
	SelectFilterMonad,
} from 'packages/search/search-page/src/common/constants/filters/select-filter/types';
import type { ProductKeys } from 'packages/search/search-page/src/common/constants/product-keys';

import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

import { i18n, pullRequestStatuses } from './messages';

const products3PFromConfig = getProducts3PFromConfigForFilter('pullRequestStatus');

const getAllPullRequestStatusOptions = (config: CloudConfig) => {
	const options = pullRequestStatuses.map((status) => ({
		value: status,
		trackingKey: status,
		queryParamValue: status,
		isDefault: false,
		label: config.intl?.formatMessage(i18n[status]) ?? '',
		product: config.product,
		isSelected: false,
		isSuggested: false,
	})) satisfies SelectFilterOption[];

	return options;
};

const fetchQueryParams = (queryParams: string[], config: CloudConfig): SelectFilterOption[] => {
	const allOptions = getAllPullRequestStatusOptions(config);
	return allOptions.filter((option) =>
		queryParams.find((queryParam) => option.value === queryParam),
	);
};

export const filter: Readonly<{
	id: 'pullRequestStatus';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => Readonly<{
			id: 'pullRequestStatus';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
		}>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'pullRequestStatus'>;
	getInitialState: (config: CloudConfig) => SelectFilterMonad<'pullRequestStatus'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'pullRequestStatus' as const,
	products: ['bitbucket', ...products3PFromConfig],
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'pullRequestStatus',
		fetcher: async (queryParams, config) => {
			return fetchQueryParams(queryParams, config);
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return getAllPullRequestStatusOptions(config);
		},
	},
});
